/**
 * Мероприятия: что проведено и что предстоит.
 *
 * Мероприятие отличается от всего остального в консоли тем, что оно
 * **кончается**. Копии идут каждую ночь и будут идти всегда; переезд случается
 * один раз — у него есть замысел, порядок, ночь исполнения и итог. Это не
 * состояние системы, а событие её жизни.
 *
 * Владелец назвал причину прямо: «оформляем в историю всю процедуру и
 * последовательность, а также результат — прям в консоли запечатываем». Через
 * год на вопрос «почему прод переехал в августе» ответит этот список, а не
 * чья-то память.
 *
 * Черновики лежат рядом с проведёнными намеренно: мероприятие, о котором
 * вспомнили в последний день, — это мероприятие, проведённое плохо.
 */
import { useQuery } from '../transport/hooks.ts';
import { useParam } from '../router.tsx';
import { Switchover } from './Switchover.tsx';
import { CampaignLog } from '../components/CampaignLog.tsx';
import { CampaignFinish } from '../components/CampaignFinish.tsx';

interface Campaign {
  id: string;
  title: string;
  kind: 'switchover' | 'plan';
  state: string;
  when: string;
  summary: string;
  steps: { title: string; hint: string }[];
  createdAt: string;
}

const TONE: Record<string, string> = {
  'идёт': 'now',
  'проведено': 'done',
  'запланировано': 'wait',
  'черновик': 'draft',
  'отменено': 'off',
};

export function Campaigns(): React.JSX.Element {
  const { data } = useQuery<{ items: Campaign[]; ahead: number }>('campaigns.list');
  /*
   * Открытое мероприятие живёт в адресе, а не в памяти вкладки.
   *
   * Владелец: «нужно компанию в путь добавить, чтобы обновление не уходило со
   * страницы». Он прав вдвойне. Ночь переключения — это часы, в которые
   * страницу перезагружают: выкатили правку, моргнула связь, нажали F5. Каждый
   * такой раз выбрасывал на список, и человек заново искал, где он был.
   *
   * И второе: адрес открытого мероприятия можно послать. «Смотри сюда» в час
   * ночи стоит дороже, чем кажется.
   */
  const [open, setOpen] = useParam('event', '');

  const items = data?.items ?? [];
  const chosen = items.find((c) => c.id === open) ?? null;

  /*
   * Открытое мероприятие показывает свой экран, а не пересказ.
   *
   * У переезда экран уже есть — с живыми шагами и кнопками. Рисовать рядом
   * второй, «для истории», значило бы завести два места, которые разойдутся.
   */
  if (chosen?.kind === 'switchover') {
    return (
      <div className="campaign__open">
        <button type="button" className="button button--ghost button--small"
                onClick={() => setOpen('')}>
          ← ко всем мероприятиям
        </button>
        <Switchover />
      </div>
    );
  }

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Мероприятия</h2>
      </header>

      <div className="panel__scroll wizard">
        {chosen ? (
          <>
            <button type="button" className="button button--ghost button--small"
                    onClick={() => setOpen('')}>
              ← ко всем мероприятиям
            </button>
            <section className="wizard__step">
              <h3 className="wizard__title">{chosen.title}</h3>
              <p className="muted">
                <b className={`camp__state camp__state--${TONE[chosen.state] ?? 'wait'}`}>
                  {chosen.state}
                </b>
                {' · '}{chosen.when}
              </p>
              <p>{chosen.summary}</p>
              <h4 className="report__head">Порядок</h4>
              <ol className="camp__steps">
                {chosen.steps.map((s) => (
                  <li key={s.title}>
                    <b>{s.title}</b>
                    <span className="muted"> — {s.hint}</span>
                  </li>
                ))}
              </ol>
              {/*
                Журнал есть и у плана: черновик, по которому уже что-то делали,
                — это не черновик, и увидеть это надо сразу.
              */}
              <CampaignFinish id={chosen.id} />
              <CampaignLog id={chosen.id} />
            </section>
          </>
        ) : (
          <>
            <p className="wizard__lead muted">
              Работы, которые делают руками и по плану. У мероприятия есть
              замысел, порядок, ночь исполнения и итог — и всё это остаётся
              здесь после того, как оно кончилось.
            </p>

            <ul className="camp">
              {items.map((c) => (
                <li key={c.id} className={`camp__item camp__item--${TONE[c.state] ?? 'wait'}`}>
                  <button type="button" className="camp__open" onClick={() => setOpen(c.id)}>
                    <span className="camp__title">{c.title}</span>
                    <span className="muted camp__meta">
                      <b className={`camp__state camp__state--${TONE[c.state] ?? 'wait'}`}>
                        {c.state}
                      </b>
                      {' · '}{c.when}
                      {' · '}{c.steps.length} шагов
                    </span>
                    <span className="muted camp__sum">{c.summary}</span>
                  </button>
                </li>
              ))}
            </ul>

            {/*
              «Запланированных нет» — полноценный ответ, и он должен быть виден.
              Пустое место на его месте читается как «не загрузилось».
            */}
            <p className="muted">
              {data?.ahead
                ? `Предстоит: ${data.ahead}.`
                : 'Запланированных мероприятий нет.'}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
